import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ActivityDetailModal, { type ActivityDetailField } from '@shared/components/ActivityDetailModal';
import Button from '@shared/components/Button';
import { openAlarmDetail } from '@/components/AlarmDetailModal';
import { severityActivityStatus, severityLabel, sourceLabel } from '@/lib/eventTypeLabels';
import { linkAccentClass } from '@/lib/uiClasses';
import { getMonitorEvent, type MonitorEventDetail } from '@/services/api';

const OPEN_EVENT = 'open-event-detail';

// Una petición que llega antes de que el modal escuche no se pierde: la página
// que la hace (p. ej. Monitor con `?event=`) puede montarse en el mismo commit
// que el layout, y sus efectos corren antes que los del modal.
let pendingId: number | null = null;
let pendingOnClose: (() => void) | null = null;

/**
 * Abre el detalle de un evento del Registro desde cualquier pantalla.
 * `onClose` corre al cerrarlo (Monitor lo usa para quitar `?event=` de la URL).
 */
export function openEventDetail(id: number, onClose?: () => void) {
  pendingId = id;
  pendingOnClose = onClose ?? null;
  window.dispatchEvent(new CustomEvent<number>(OPEN_EVENT, { detail: id }));
}

/**
 * Detalle de un evento del Registro (`GET /api/monitor/events/{id}`). Un solo
 * modal, montado en el layout, para el Registro de Monitor, la ficha del taller
 * y los eventos de facturación: se abre con `openEventDetail(id)` (evento de
 * ventana `open-event-detail`). Pinta con el `ActivityDetailModal` compartido
 * (el mismo que el historial de tareas del taller); lo propio del evento va en
 * `extraFields` y `description`.
 */
export default function EventDetailModal() {
  const [eventId, setEventId] = useState<number | null>(null);
  const [event, setEvent] = useState<MonitorEventDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleOpen = (e: Event) => {
      pendingId = null;
      setEventId((e as CustomEvent<number>).detail);
    };
    window.addEventListener(OPEN_EVENT, handleOpen);
    if (pendingId != null) {
      setEventId(pendingId);
      pendingId = null;
    }
    return () => window.removeEventListener(OPEN_EVENT, handleOpen);
  }, []);

  useEffect(() => {
    if (eventId == null) return;
    let cancelled = false;
    setEvent(null);
    setError('');
    setLoading(true);
    getMonitorEvent(eventId)
      .then((d) => {
        if (!cancelled) setEvent(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'No se pudo cargar el evento');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const close = () => {
    const onClose = pendingOnClose;
    pendingId = null;
    pendingOnClose = null;
    setEventId(null);
    setEvent(null);
    setError('');
    onClose?.();
  };

  const detail = event
    ? {
        // La severidad pintada es la de la FILA, no la del tipo.
        status: severityActivityStatus(event.severity),
        statusLabel: severityLabel(event.severity),
        createdAt: event.created_at ?? undefined,
        performedBy: event.performed_by || 'Sistema',
        description: event.description ?? '',
        // Un objeto vacío no merece el panel de datos técnicos.
        metadata: Object.keys(event.metadata ?? {}).length > 0 ? event.metadata : undefined,
      }
    : null;

  const extraFields: ActivityDetailField[] = event
    ? [
        { icon: 'category', label: 'Categoría', value: event.category_label || '—' },
        { icon: 'route', label: 'Origen', value: sourceLabel(event.source) || '—' },
        {
          icon: 'business',
          label: 'Taller',
          value:
            event.workshop_id != null ? (
              <Link to={`/talleres/${event.workshop_id}`} onClick={close} className={`${linkAccentClass} block truncate`}>
                {event.workshop_name || event.workshop_slug || `#${event.workshop_id}`}
              </Link>
            ) : (
              '—'
            ),
        },
        ...(event.alarm_id != null
          ? ([
              {
                icon: 'notification_important',
                label: 'Alarma',
                value: (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    icon="open_in_new"
                    onClick={() => {
                      // El detalle de la alarma queda por debajo de éste
                      // (z-index): se cierra el evento antes de abrirla.
                      const alarmId = event.alarm_id as number;
                      close();
                      openAlarmDetail(alarmId);
                    }}
                  >
                    {`Alarma #${event.alarm_id}`}
                  </Button>
                ),
              },
            ] satisfies ActivityDetailField[])
          : []),
        {
          icon: 'fingerprint',
          label: 'ID de petición',
          fullWidth: true,
          value: <span className="block font-mono text-xs break-all">{event.request_id || '—'}</span>,
        },
      ]
    : [];

  return (
    <ActivityDetailModal
      isOpen={eventId != null}
      onClose={close}
      loading={loading}
      error={error || undefined}
      title={event?.label || 'Detalles del evento'}
      subtitle={eventId != null ? `#${eventId}${event ? ` · ${event.event_type}` : ''}` : undefined}
      detail={detail}
      extraFields={extraFields}
      size="auto"
      labels={{
        loading: 'Cargando evento…',
        status: 'Severidad',
        createdAt: 'Registrado el',
        performedBy: 'Autor',
        description: 'Descripción',
        noDescription: 'Sin descripción',
        techData: 'Datos técnicos (JSON)',
      }}
    />
  );
}
